import { reactive, computed } from "vue"

const LIKES_KEY = "ampelos:likes"

export const store = reactive({
  loaded: false,
  grapes: [],        // all (incl. ancestor stubs)
  edges: [],         // similarity edges between full varieties
  compounds: [],
  wheel: { categories: [] },
  selectedSlug: null,
  view: "network",   // network | lineage | compounds | wheel | wine | layers
  sheetCollapsed: false, // mobile: grape sheet minimised to a bottom bar
  chatOpen: false,
  chatSeed: null,    // question to prefill in the chat input (not auto-sent)
  pendingShareId: null, // /w/:id share link waiting to be loaded by WineView
  wineResult: null,  // last Taste-a-wine analysis; survives view switches, feeds chat context
  likes: new Set(JSON.parse(localStorage.getItem(LIKES_KEY) || "[]")),
})

export async function loadAll() {
  const [net, compounds, wheel] = await Promise.all([
    fetch("/api/network").then(r => r.json()),
    fetch("/api/compounds").then(r => r.json()),
    fetch("/api/wheel").then(r => r.json()),
  ])
  // network returns full varieties; fetch complete grape list (with ancestors) too
  const grapes = await fetch("/api/grapes").then(r => r.json())
  store.grapes = grapes
  store.edges = net.edges
  store.compounds = compounds
  store.wheel = wheel
  store.loaded = true
}

export const bySlug = computed(() => {
  const m = new Map()
  for (const g of store.grapes) m.set(g.slug, g)
  return m
})

export const fullGrapes = computed(() => store.grapes.filter(g => !g.ancestor_only))

export const selectedGrape = computed(() =>
  store.selectedSlug ? bySlug.value.get(store.selectedSlug) : null
)

export function selectGrape(slug) {
  store.selectedSlug = slug
  store.sheetCollapsed = false
}

// Navigate to a view with the current grape kept in focus. On mobile the
// sheet collapses to a bottom bar so the view is actually visible; on
// desktop the sidebar stays put (CSS ignores the collapsed state there).
export function exploreView(view) {
  store.view = view
  store.sheetCollapsed = true
}

export function toggleLike(slug) {
  if (store.likes.has(slug)) store.likes.delete(slug)
  else store.likes.add(slug)
  localStorage.setItem(LIKES_KEY, JSON.stringify([...store.likes]))
}

// Recommendation scores: for each unliked grape, sum of similarity-edge
// weights connecting it to liked grapes. This is the "grapes you'd likely
// enjoy but haven't tried" mechanism from the capture note.
export const recoScores = computed(() => {
  const scores = new Map()
  if (store.likes.size === 0) return scores
  for (const e of store.edges) {
    const sLiked = store.likes.has(e.source)
    const tLiked = store.likes.has(e.target)
    if (sLiked && !tLiked) scores.set(e.target, (scores.get(e.target) || 0) + e.weight)
    if (tLiked && !sLiked) scores.set(e.source, (scores.get(e.source) || 0) + e.weight)
  }
  return scores
})

export const topRecos = computed(() =>
  [...recoScores.value.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6).map(([slug, score]) => ({ slug, score }))
)

// children lookup for the lineage view
export const childrenOf = computed(() => {
  const m = new Map()
  for (const g of store.grapes) {
    for (const p of [g.parent1_slug, g.parent2_slug]) {
      if (!p) continue
      if (!m.has(p)) m.set(p, [])
      m.get(p).push(g.slug)
    }
  }
  return m
})

// Berry (skin) colour of the variety — not the colour of the wine, which is a
// winemaking choice. noir / gris / blanc, as ampelographers classify them.
export const BERRY_COLORS = { red: "#c04a58", gris: "#c98a6d", white: "#d9c778" }
export const berryColor = c => BERRY_COLORS[c] || "#8a8a8a"
export const berryLabel = c => ({ red: "noir", gris: "gris", white: "blanc" }[c] || c)

export function askClaude(question) {
  store.chatSeed = question
  store.chatOpen = true
}
